// =============================================================================
// SubbieHubScreens — T-381 Slice 1.
// Subbies out of the HR/People nesting and into their own home:
//   • /subbies              — every subcontractor at a glance (state, expiries)
//   • /subbies/:companyId   — ONE page per subbie: details, compliance
//     snapshot, insurances, CLIENT APPROVALS (new — migration 219), rate
//     cards (via linked supplier), recent bookings.
// Reuses what already exists: v_subbie_compliance (mig 196) for state,
// NewSubbieCompanyScreen for create/edit, the Compliance Centre for
// fleet-wide actions. This is the per-company cockpit.
//
// 15 Sep 2026 — insurance save was failing with
// "violates check constraint vendor_insurances_policy_type_check": the
// dropdown sent 'motor_vehicle' but the table only allows public_liability /
// workers_comp / professional_indemnity / plant_all_risk / motor / other.
// POLICY_TYPES now mirrors the constraint exactly (plant_all_risk added), and
// the list renders labels instead of raw codes.
//
// 15 Sep 2026 (evening) — the certificate of currency is READ on attach:
// upload straight to ap-documents, POST /vendor-insurances/ai-extract, and the
// policy row pre-fills (type, insurer, number, cover, expiry) with a confidence
// chip and an "insured name" check against the subbie's name. Save still needs
// a human. Nothing changes for a subbie with no certificate to attach.
//
// 15 Sep 2026 (late) — worker "+ ticket" has Front / Back like the staff
// licence wizard. The front uploads on pick and goes through
// /licences/ai-extract (bucket ap-documents) to pre-fill type / number /
// issued / expiry, with a confidence chip and a name-on-card check; the back
// is upload-only and lands in back_document_path (mig 377). One scanned file
// showing both sides ticks "both sides" and the back points at the same file.
// =============================================================================

import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, HardHat, Plus, Search, FileText, Upload,
  Trash2, BadgeCheck, AlertTriangle, CheckCircle2, XCircle, Sparkles,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, KPI, Input, Field } from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import SupplierMoneyCard from "./SupplierMoneyCard";

function stateTone(state: string): { label: string; tone: any } {
  switch (state) {
    case "ok":               return { label: "Compliant",      tone: "won" };
    case "expiring":         return { label: "Expiring soon",  tone: "warning" };
    case "expired":          return { label: "Expired",        tone: "lost" };
    case "no_insurance":     return { label: "No insurance",   tone: "lost" };
    case "pending_approval": return { label: "Pending approval", tone: "pending" };
    default:                 return { label: state, tone: "default" };
  }
}

function expiryClass(d: string | null): string {
  if (!d) return "text-text-muted";
  const days = Math.round((new Date(d).getTime() - Date.now()) / 86400000);
  if (days < 0) return "text-danger";
  if (days <= 30) return "text-warn";
  return "text-text";
}

// -----------------------------------------------------------------------------
// /subbies — the fleet list
// -----------------------------------------------------------------------------
export function SubbieHubListScreen() {
  const user = useRequireAuth();
  const [q, setQ] = useState("");

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["subbie_hub_list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_subbie_compliance")
        .select("*")
        .order("legal_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      [r.legal_name, r.trading_name, r.company_code, r.abn].filter(Boolean)
        .some((v: string) => String(v).toLowerCase().includes(t)));
  }, [rows, q]);

  const kpis = useMemo(() => ({
    total: rows.length,
    ok: rows.filter(r => r.state === "ok").length,
    attention: rows.filter(r => r.state !== "ok").length,
  }), [rows]);

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return <div className="p-6 max-w-2xl mx-auto"><Card><EmptyState icon={HardHat} title="Managers only" description="The subbie hub is restricted to manager-and-above roles." /></Card></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto pb-20">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <HardHat size={20} className="text-accent" /> Subcontractors
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Every subbie in one place — click through for compliance, insurances, client approvals + rates.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/people/subbies/new"><Button icon={Plus}>New subbie</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <KPI label="Subbies" value={kpis.total} icon={HardHat} />
        <KPI label="Compliant" value={kpis.ok} icon={BadgeCheck} />
        <KPI label="Need attention" value={kpis.attention} icon={AlertTriangle} accent={kpis.attention > 0} />
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / code / ABN…" className="pl-9" />
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : error ? (
        <Card><div className="p-5 text-[13px] text-danger">Couldn't load subbies: {(error as any).message}</div></Card>
      ) : filtered.length === 0 ? (
        <Card><EmptyState icon={HardHat} title="No subbies" description={rows.length ? "None match the search." : "Add your first subcontractor."} /></Card>
      ) : (
        <>
          {/* Needs-attention board — the old Compliance Centre's job, as a
              section, not a second page. Red-accented, sorted worst-first. */}
          {filtered.some(r => r.state !== "ok") && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={13} className="text-danger" />
                <span className="text-[11px] uppercase tracking-wider2 font-semibold text-danger">Needs attention</span>
              </div>
              <div className="space-y-2">
                {filtered.filter(r => r.state !== "ok").map(r => <SubbieRow key={r.subcontractor_company_id} r={r} attention />)}
              </div>
            </div>
          )}
          {filtered.some(r => r.state === "ok") && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BadgeCheck size={13} className="text-ok" />
                <span className="text-[11px] uppercase tracking-wider2 font-semibold text-text-muted">Compliant</span>
              </div>
              <div className="space-y-2">
                {filtered.filter(r => r.state === "ok").map(r => <SubbieRow key={r.subcontractor_company_id} r={r} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// One subbie row — red left edge + what's actually wrong when it needs eyes.
function SubbieRow({ r, attention = false }: { r: any; attention?: boolean }) {
  const st = stateTone(r.state);
  const problems: string[] = [];
  if (attention) {
    if (!r.is_approved) problems.push("awaiting Coretel approval");
    if (!r.pl_expiry && !r.wc_expiry && !r.pi_expiry) problems.push("no insurance on file");
    else {
      if (r.pl_expiry && new Date(r.pl_expiry) < new Date()) problems.push("PL expired");
      if (r.wc_expiry && new Date(r.wc_expiry) < new Date()) problems.push("WC expired");
      if (r.pi_expiry && new Date(r.pi_expiry) < new Date()) problems.push("PI expired");
    }
  }
  return (
    <Link to={`/subbies/${r.subcontractor_company_id}`} className="block">
      <Card className={attention ? "border-l-2 border-l-danger" : "border-l-2 border-l-ok/60"}>
        <div className="p-4 flex items-center gap-3 flex-wrap hover:bg-ink-raised/40 rounded-lg transition-colors">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] text-accent">{r.company_code}</span>
              <span className="text-[14px] font-medium text-text">{r.trading_name || r.legal_name}</span>
              <Pill label={st.label} tone={st.tone} small />
              {Number(r.active_bookings) > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${attention ? "bg-danger/10 border-danger/40 text-danger font-medium" : "bg-accent/10 border-accent/30 text-accent"}`}>
                  on {r.active_bookings} job{Number(r.active_bookings) === 1 ? "" : "s"}{attention ? " — check before site" : ""}
                </span>
              )}
            </div>
            {attention && problems.length > 0 ? (
              <div className="text-[12px] text-danger mt-1">{problems.join(" · ")}</div>
            ) : (
              <div className="text-[11px] text-text-muted mt-1 flex flex-wrap gap-x-4">
                <span>PL <span className={expiryClass(r.pl_expiry)}>{r.pl_expiry ? fmtDate(r.pl_expiry) : "—"}</span></span>
                <span>WC <span className={expiryClass(r.wc_expiry)}>{r.wc_expiry ? fmtDate(r.wc_expiry) : "—"}</span></span>
                <span>PI <span className={expiryClass(r.pi_expiry)}>{r.pi_expiry ? fmtDate(r.pi_expiry) : "—"}</span></span>
              </div>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
}

// -----------------------------------------------------------------------------
// /subbies/:companyId — the per-subbie cockpit
// -----------------------------------------------------------------------------
export function SubbieCompanyHubScreen() {
  const { companyId } = useParams<{ companyId: string }>();
  const user = useRequireAuth();
  const qc = useQueryClient();

  const { data: sub, isLoading } = useQuery({
    queryKey: ["subbie_hub", companyId],
    enabled: !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_subbie_compliance").select("*")
        .eq("subcontractor_company_id", companyId!).maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  // Insurances — company-first as of mig 221 (supplier-keyed legacy rows
  // included via or-filter so nothing vanishes).
  const { data: insurances = [] } = useQuery({
    queryKey: ["subbie_hub_ins", companyId, sub?.supplier_id],
    enabled: !!sub,
    queryFn: async () => {
      let orFilter = `subcontractor_company_id.eq.${companyId}`;
      if (sub.supplier_id) orFilter += `,and(subcontractor_company_id.is.null,supplier_id.eq.${sub.supplier_id})`;
      const { data } = await supabase.from("vendor_insurances")
        .select("*").or(orFilter).order("expiry_date");
      return (data ?? []) as any[];
    },
  });

  const { data: rateCards = [] } = useQuery({
    queryKey: ["subbie_hub_rates", sub?.supplier_id],
    enabled: !!sub?.supplier_id,
    queryFn: async () => {
      const { data } = await supabase.from("rate_cards")
        .select("rate_card_id, rate_card_code, display_name, is_active")
        .eq("supplier_id", sub.supplier_id).eq("rate_card_type", "supplier_sor")
        .order("is_active", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const { data: bookings = [] } = useQuery({
    queryKey: ["subbie_hub_bookings", sub?.supplier_id],
    enabled: !!sub?.supplier_id,
    queryFn: async () => {
      const { data } = await supabase.from("project_bookings")
        .select("booking_id, title, status, scheduled_start, projects:project_id(project_code, project_name)")
        .eq("supplier_id", sub.supplier_id)
        .order("scheduled_start", { ascending: false }).limit(8);
      return (data ?? []) as any[];
    },
  });

  if (!user) return null;
  if (isLoading) return <div className="py-12 flex justify-center"><Spinner /></div>;
  if (!sub) {
    return <div className="p-6 max-w-2xl mx-auto"><Card><EmptyState icon={HardHat} title="Subbie not found" description="It may have been removed." /></Card></div>;
  }
  const st = stateTone(sub.state);

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto pb-20">
      <Link to="/subbies" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"><ArrowLeft size={14} /> Subcontractors</Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[12px] text-accent">{sub.company_code}</span>
            <Pill label={st.label} tone={st.tone} small />
            <Pill label={sub.is_approved ? "approved" : "not approved"} tone={sub.is_approved ? "won" : "pending"} small />
          </div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight mt-1">{sub.trading_name || sub.legal_name}</h1>
          <div className="text-[12px] text-text-muted mt-0.5 flex flex-wrap gap-x-4">
            {sub.abn && <span>ABN {sub.abn}</span>}
            {sub.primary_contact_name && <span>{sub.primary_contact_name}{sub.primary_contact_phone ? ` · ${sub.primary_contact_phone}` : ""}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/people/subbies/${sub.subcontractor_company_id}`}><Button variant="secondary" size="sm">Edit details</Button></Link>
          {hasRole(user, "manager") && (
            <Button variant="secondary" size="sm" icon={Trash2} onClick={async () => {
              if (!window.confirm(`Archive ${sub.trading_name || sub.legal_name}? It drops off the hub (data kept — reactivate via Edit details).`)) return;
              const { error } = await supabase.from("subcontractor_companies")
                .update({ is_active: false }).eq("subcontractor_company_id", sub.subcontractor_company_id);
              if (error) { window.alert(error.message); return; }
              window.location.href = "/subbies";
            }}>Archive</Button>
          )}
        </div>
      </div>

      {/* Compliance checklist — Coretel's MINIMUM to be 100% in our system:
          PL current at ≥$20m, Workers Comp current, Coretel approved. */}
      <ComplianceChecklist sub={sub} insurances={insurances} />

      {/* T-385 — money picture (via linked supplier): owing / late / POs / spend */}
      {sub.supplier_id && <SupplierMoneyCard supplierId={sub.supplier_id} />}

      {/* Two-column management centre: PEOPLE on the left, PAPER on the right. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div>
          {/* Workers + tickets — T-381 Slice 2 (fields mirror the Ventia RRF) */}
          <WorkersCard companyId={sub.subcontractor_company_id} canEdit={hasRole(user, "manager")} />
          {/* Client approvals — T-363 folded in */}
          <ClientApprovalsCard companyId={sub.subcontractor_company_id} canEdit={hasRole(user, "manager")} />
        </div>
        <div>
          {/* Insurances — managed HERE (the compliance home). Mig 221. */}
          <InsurancesCard companyId={sub.subcontractor_company_id} supplierId={sub.supplier_id} insurances={insurances} canEdit={hasRole(user, "manager")}
                          companyName={sub.trading_name || sub.legal_name || ""} />
          {/* Approve / suspend / history — absorbed from the old centre */}
          <DecisionsCard companyId={sub.subcontractor_company_id} isApproved={!!sub.is_approved} canEdit={hasRole(user, "manager")} />
        </div>
      </div>

      {/* Rate cards (T-384 tie-in) */}
      <Card className="mb-4">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Rate cards (SoR)</div>
            <Link to="/rates/import-pdf" className="text-[11px] text-accent hover:underline">import rates PDF</Link>
          </div>
          {!sub.supplier_id ? (
            <LinkSupplierInline companyId={sub.subcontractor_company_id} />
          ) : rateCards.length === 0 ? (
            <div className="text-[12px] text-text-muted">No rate card loaded — import their SoR and it becomes pickable in the quote builder.</div>
          ) : (
            <div className="space-y-1">
              {rateCards.map((rc: any) => (
                <Link key={rc.rate_card_id} to={`/rates/${rc.rate_card_id}`} className="text-[12px] flex items-center gap-3 py-1 border-b border-line last:border-b-0 hover:text-accent">
                  <span className="font-mono text-accent">{rc.rate_card_code}</span>
                  <span className="text-text flex-1">{rc.display_name}</span>
                  <Pill label={rc.is_active ? "active" : "inactive"} tone={rc.is_active ? "won" : "lost"} small />
                </Link>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Recent bookings */}
      {bookings.length > 0 && (
        <Card>
          <div className="p-4">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Recent bookings</div>
            <div className="space-y-1">
              {bookings.map((b: any) => (
                <div key={b.booking_id} className="text-[12px] flex items-center gap-3 py-1 border-b border-line last:border-b-0">
                  <span className="font-mono text-accent">{b.projects?.project_code ?? "—"}</span>
                  <span className="text-text flex-1 min-w-0 truncate">{b.title}</span>
                  <Pill label={b.status} tone={b.status === "confirmed" ? "won" : "pending"} small />
                  <span className="text-text-muted font-mono">{b.scheduled_start ? fmtDate(b.scheduled_start) : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ComplianceChecklist — "what does it take to be 100% in OUR system", with
// ticks. Minimums per James: PL current & ≥$20m, Workers Comp current,
// Coretel approved. Extras shown as info (never block the percentage).
// -----------------------------------------------------------------------------
const PL_MIN_COVER = 20_000_000;

function ComplianceChecklist({ sub, insurances }: { sub: any; insurances: any[] }) {
  const today = new Date().toISOString().slice(0, 10);
  const latest = (type: string) =>
    insurances.filter(i => i.policy_type === type && i.expiry_date)
      .sort((a, b) => String(b.expiry_date).localeCompare(String(a.expiry_date)))[0] ?? null;

  const pl = latest("public_liability");
  const wc = latest("workers_comp");

  const plCurrent = !!pl && pl.expiry_date >= today;
  const plCover   = !!pl && Number(pl.cover_amount ?? 0) >= PL_MIN_COVER;
  const wcCurrent = !!wc && wc.expiry_date >= today;

  const checks: { label: string; ok: boolean; detail: string }[] = [
    {
      label: "Public liability — current, ≥ $20m",
      ok: plCurrent && plCover,
      detail: !pl ? "no policy on file"
        : !plCurrent ? `expired ${fmtDate(pl.expiry_date)}`
        : !plCover ? (pl.cover_amount ? `cover $${Number(pl.cover_amount).toLocaleString()} — below $20m minimum` : "cover amount not recorded — add it to tick this off")
        : `$${Number(pl.cover_amount).toLocaleString()} · expires ${fmtDate(pl.expiry_date)}`,
    },
    {
      label: "Workers compensation — current",
      ok: wcCurrent,
      detail: !wc ? "no policy on file" : wcCurrent ? `expires ${fmtDate(wc.expiry_date)}` : `expired ${fmtDate(wc.expiry_date)}`,
    },
    {
      label: "Coretel approval",
      ok: !!sub.is_approved,
      detail: sub.is_approved ? `approved${sub.approved_at ? ` ${fmtDate(sub.approved_at)}` : ""}` : "approve below once the paperwork checks out",
    },
  ];
  const done = checks.filter(c => c.ok).length;
  const pct = Math.round((done / checks.length) * 100);
  const complete = done === checks.length;

  return (
    <Card className={`mb-4 ${complete ? "border-ok/50" : "border-warn/40"}`}>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Compliance checklist</div>
          <div className="flex items-center gap-2">
            <div className="w-28 h-1.5 rounded-full bg-ink-raised overflow-hidden">
              <div className={`h-full ${complete ? "bg-ok" : "bg-warn"}`} style={{ width: `${pct}%` }} />
            </div>
            <span className={`text-[13px] font-semibold ${complete ? "text-ok" : "text-warn"}`}>
              {complete ? "100% compliant" : `${done}/${checks.length}`}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {checks.map(c => (
            <div key={c.label} className={`rounded-md border px-3 py-2 ${c.ok ? "border-ok/40 bg-ok/5" : "border-danger/40 bg-danger/5"}`}>
              <div className="flex items-center gap-1.5 text-[12px] font-medium">
                {c.ok ? <CheckCircle2 size={14} className="text-ok flex-shrink-0" /> : <XCircle size={14} className="text-danger flex-shrink-0" />}
                <span className={c.ok ? "text-text" : "text-danger"}>{c.label}</span>
              </div>
              <div className="text-[11px] text-text-muted mt-0.5 ml-5">{c.detail}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// InsurancesCard — FULL insurance management on the subbie (mig 221). Add /
// renew / delete policies + cert upload, all here. Never bounces to Suppliers.
// -----------------------------------------------------------------------------
// Values MUST match vendor_insurances_policy_type_check in the DB — a value
// the constraint doesn't know fails the insert with a raw Postgres error.
const POLICY_TYPES = [
  { v: "public_liability",       label: "Public liability" },
  { v: "workers_comp",           label: "Workers compensation" },
  { v: "professional_indemnity", label: "Professional indemnity" },
  { v: "motor",                  label: "Motor vehicle" },
  { v: "plant_all_risk",         label: "Plant all risk" },
  { v: "other",                  label: "Other" },
];
const policyLabel = (v: string | null | undefined) =>
  POLICY_TYPES.find(x => x.v === v)?.label ?? String(v ?? "").replace(/_/g, " ");

function InsurancesCard({ companyId, supplierId, insurances, canEdit, companyName = "" }: {
  companyId: string; supplierId: string | null; insurances: any[]; canEdit: boolean; companyName?: string;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [p, setP] = useState<any>({ policy_type: "public_liability", insurer: "", policy_number: "", cover_amount: "", expiry_date: "" });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // AI read of the certificate — mirrors the licence wizard's upload-on-pick.
  type AiStatus = "idle" | "uploading" | "reading" | "done" | "error";
  const [aiStatus, setAiStatus] = useState<AiStatus>("idle");
  const [aiConf, setAiConf] = useState<number | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const [aiInsured, setAiInsured] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [certPath, setCertPath] = useState<string | null>(null);   // uploaded on pick; save reuses it

  const insuredLooksRight = (() => {
    if (!aiInsured || !companyName) return null;
    const norm = (v: string) => v.toLowerCase().replace(/pty|ltd|limited|p\/l|t\/as|trading as|[^a-z0-9 ]/g, " ").split(/\s+/).filter(w => w.length > 2);
    const a = norm(aiInsured), b = norm(companyName);
    return b.some(w => a.includes(w));
  })();

  async function pickFile(f: File | null) {
    setFile(f); setCertPath(null); setAiStatus("idle"); setAiConf(null); setAiNote(null); setAiInsured(null); setAiError(null);
    if (!f) return;
    try {
      setAiStatus("uploading");
      const safe = f.name.replace(/[^\w.\-]/g, "_");
      const path = `vendor-compliance/${companyId}/${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage.from("ap-documents").upload(path, f, { upsert: false });
      if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
      setCertPath(path);

      setAiStatus("reading");
      const res = await apiFetch("/vendor-insurances/ai-extract", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ storage_path: path }),
      });
      if (!res.ok) { const txt = await res.text().catch(() => ""); throw new Error(`Couldn't read the certificate (${res.status}): ${txt.slice(0, 200)}`); }
      const ai = await res.json() as {
        confidence: number;
        extracted: { policy_type: string; insurer: string | null; policy_number: string | null; cover_amount: number | null;
                     expiry_date: string | null; insured_name: string | null; notes: string | null };
      };
      const x = ai.extracted;
      // Pre-fill only what the manager hasn't typed — same courtesy as the licence wizard.
      setP((prev: any) => ({
        ...prev,
        policy_type:   x.policy_type && POLICY_TYPES.some(t => t.v === x.policy_type) ? x.policy_type : prev.policy_type,
        insurer:       prev.insurer || (x.insurer ?? ""),
        policy_number: prev.policy_number || (x.policy_number ?? ""),
        cover_amount:  prev.cover_amount || (x.cover_amount != null ? String(x.cover_amount) : ""),
        expiry_date:   prev.expiry_date || (x.expiry_date ?? ""),
      }));
      setAiConf(ai.confidence); setAiNote(x.notes); setAiInsured(x.insured_name);
      setAiStatus("done");
    } catch (e: any) {
      setAiStatus("error"); setAiError(e?.message ?? "Couldn't read it");
    }
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["subbie_hub_ins"] });
    qc.invalidateQueries({ queryKey: ["subbie_hub", companyId] });
    qc.invalidateQueries({ queryKey: ["subbie_hub_list"] });
  };

  async function save() {
    if (!p.expiry_date) { window.alert("Set the policy expiry date — it drives the compliance state."); return; }
    setBusy(true);
    try {
      let savedCertPath: string | null = certPath;   // already uploaded on pick (AI read)
      if (file && !savedCertPath) {
        const safe = file.name.replace(/[^\w.\-]/g, "_");
        savedCertPath = `vendor-compliance/${companyId}/${Date.now()}-${safe}`;
        const { error: upErr } = await supabase.storage.from("ap-documents").upload(savedCertPath, file, { upsert: false });
        if (upErr) { savedCertPath = null; window.alert(`Cert upload failed (${upErr.message}) — saving the policy without it.`); }
      }
      const { error } = await supabase.from("vendor_insurances").insert({
        subcontractor_company_id: companyId,
        supplier_id: supplierId,                 // harmless when linked; null OK (mig 221)
        policy_type: p.policy_type,
        insurer: p.insurer.trim() || null,
        policy_number: p.policy_number.trim() || null,
        cover_amount: p.cover_amount ? Number(p.cover_amount) : null,
        expiry_date: p.expiry_date,
        certificate_file_path: savedCertPath,
        notes: aiInsured ? `Certificate read by AI · insured: ${aiInsured}` : null,
      });
      if (error) throw error;
      setP({ policy_type: "public_liability", insurer: "", policy_number: "", cover_amount: "", expiry_date: "" });
      setFile(null); setCertPath(null); setAiStatus("idle"); setAiConf(null); setAiNote(null); setAiInsured(null); setAiError(null); setAdding(false);
      invalidate();
    } catch (e: any) { window.alert(`Couldn't save policy: ${e.message ?? e}`); }
    finally { setBusy(false); }
  }

  async function viewCert(path: string) {
    const { data } = await supabase.storage.from("ap-documents").createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  }

  async function remove(i: any) {
    if (!window.confirm(`Remove this ${policyLabel(i.policy_type)} policy record?`)) return;
    await supabase.from("vendor_insurances").delete().eq("insurance_id", i.insurance_id);
    invalidate();
  }

  return (
    <Card className="mb-4">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Insurances</div>
          {canEdit && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(a => !a)}>{adding ? "Close" : "Add / renew policy"}</Button>}
        </div>

        {adding && (
          <div className="mb-3 border border-line rounded-md p-3 bg-ink-raised space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Field label="Policy">
                <select value={p.policy_type} onChange={e => setP({ ...p, policy_type: e.target.value })}
                  className="w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                  {POLICY_TYPES.map(x => <option key={x.v} value={x.v}>{x.label}</option>)}
                </select>
              </Field>
              <Field label="Insurer" hint=""><Input value={p.insurer} onChange={e => setP({ ...p, insurer: e.target.value })} /></Field>
              <Field label="Policy #" hint=""><Input value={p.policy_number} onChange={e => setP({ ...p, policy_number: e.target.value })} /></Field>
              <Field label="Cover $" hint=""><Input type="number" value={p.cover_amount} onChange={e => setP({ ...p, cover_amount: e.target.value })} /></Field>
              <Field label="Expiry"><Input type="date" value={p.expiry_date} onChange={e => setP({ ...p, expiry_date: e.target.value })} /></Field>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => fileInput.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-[12px] text-text hover:border-accent transition-colors">
                <Upload size={13} /> {file ? file.name : "Attach certificate of currency"}
              </button>
              <input ref={fileInput} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => { pickFile(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
              <Button size="sm" disabled={busy || aiStatus === "uploading" || aiStatus === "reading"} onClick={save}>{busy ? "Saving…" : "Save policy"}</Button>
              {aiStatus === "uploading" && <span className="inline-flex items-center gap-1 text-[11px] text-text-muted"><Spinner size={11} /> uploading…</span>}
              {aiStatus === "reading"   && <span className="inline-flex items-center gap-1 text-[11px] text-text-muted"><Sparkles size={11} className="text-accent" /> reading the certificate…</span>}
              {aiStatus === "done" && aiConf != null && (
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${aiConf < 0.6 ? "border-warn/30 text-warn bg-warn/5" : "border-ok/30 text-ok bg-ok/5"}`}>
                  <Sparkles size={11} /> read — {Math.round(aiConf * 100)}% sure, check it
                </span>
              )}
              {aiStatus === "done" && aiInsured && insuredLooksRight === false && (
                <span className="inline-flex items-center gap-1 rounded-full border border-warn/30 text-warn bg-warn/5 px-2 py-0.5 text-[11px]">
                  <AlertTriangle size={11} /> insured on certificate: {aiInsured}
                </span>
              )}
              {aiStatus === "error" && <span className="text-[11px] text-danger">{aiError} — fill it in by hand.</span>}
            </div>
            {aiNote && aiStatus === "done" && <div className="text-[11px] text-text-muted italic">AI: {aiNote}</div>}
          </div>
        )}

        {insurances.length === 0 ? (
          <div className="text-[12px] text-text-muted">No policies on file — add their certificates of currency here.</div>
        ) : (
          <div className="space-y-1">
            {insurances.map((i: any) => (
              <div key={i.insurance_id} className="text-[12px] flex items-center gap-3 py-1.5 border-b border-line last:border-b-0 flex-wrap">
                <span className="font-medium text-text w-44">{policyLabel(i.policy_type)}</span>
                <span className="text-text-muted">{i.insurer ?? "—"}{i.policy_number ? ` · ${i.policy_number}` : ""}</span>
                {i.cover_amount && <span className="text-text-muted font-mono">${Number(i.cover_amount).toLocaleString()}</span>}
                <span className={`ml-auto font-mono ${expiryClass(i.expiry_date)}`}>{i.expiry_date ? fmtDate(i.expiry_date) : "no expiry"}</span>
                {i.certificate_file_path && (
                  <button type="button" onClick={() => viewCert(i.certificate_file_path)} className="text-accent hover:underline inline-flex items-center gap-1"><FileText size={12} /> cert</button>
                )}
                {canEdit && (
                  <button type="button" onClick={() => remove(i)} className="text-text-muted hover:text-danger p-1"><Trash2 size={12} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// LinkSupplierInline — one company, two hats: joins the subbie record to its
// AP/rates supplier record right where the gap shows (insurances card).
// -----------------------------------------------------------------------------
function LinkSupplierInline({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const [supplierId, setSupplierId] = useState("");
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers_for_subbie_link"],
    queryFn: async () => {
      const { data } = await supabase.from("suppliers")
        .select("supplier_id, legal_name, trading_name").eq("is_active", true).order("legal_name");
      return (data ?? []) as any[];
    },
  });
  const link = useMutation({
    mutationFn: async () => {
      if (!supplierId) throw new Error("Pick the supplier record to link.");
      const { error } = await supabase.from("subcontractor_companies")
        .update({ supplier_id: supplierId }).eq("subcontractor_company_id", companyId);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["subbie_hub", companyId] }); },
    onError: (e: any) => window.alert(e?.message ?? String(e)),
  });
  return (
    <div className="flex items-center gap-2 flex-wrap text-[12px]">
      <span className="text-text-muted">No supplier record linked — pick their AP/rates record so certs, bills + rate cards join up:</span>
      <select value={supplierId} onChange={e => setSupplierId(e.target.value)}
        className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
        <option value="">— supplier —</option>
        {suppliers.map((s: any) => <option key={s.supplier_id} value={s.supplier_id}>{s.trading_name || s.legal_name}</option>)}
      </select>
      <Button size="sm" variant="secondary" disabled={link.isPending || !supplierId} onClick={() => link.mutate()}>Link</Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Decisions card — approve/suspend/request-info + history, absorbed from the
// Compliance Centre modal so the cockpit is the one place to act.
// -----------------------------------------------------------------------------
function DecisionsCard({ companyId, isApproved, canEdit }: { companyId: string; isApproved: boolean; canEdit: boolean }) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [note, setNote] = useState("");

  const { data: events = [] } = useQuery({
    queryKey: ["subbie_compliance_events", companyId],
    queryFn: async () => {
      const { data } = await supabase.from("subbie_compliance_events")
        .select("event_id, event_type, note, created_at")
        .eq("subcontractor_company_id", companyId)
        .order("created_at", { ascending: false }).limit(10);
      return (data ?? []) as any[];
    },
  });

  const act = useMutation({
    mutationFn: async (type: string) => {
      if (type === "approved" || type === "suspended") {
        const patch: any = { is_approved: type === "approved" };
        if (type === "approved") {
          patch.approved_by_user_id = (user as any)?.user_id ?? null;
          patch.approved_at = new Date().toISOString();
        }
        const { error: uErr } = await supabase.from("subcontractor_companies")
          .update(patch).eq("subcontractor_company_id", companyId);
        if (uErr) throw uErr;
      }
      const { error } = await supabase.from("subbie_compliance_events").insert({
        subcontractor_company_id: companyId,
        event_type: type,
        note: note.trim() || null,
        created_by: (user as any)?.user_id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setNote("");
      qc.invalidateQueries({ queryKey: ["subbie_hub", companyId] });
      qc.invalidateQueries({ queryKey: ["subbie_compliance_events", companyId] });
      qc.invalidateQueries({ queryKey: ["subbie_hub_list"] });
    },
    onError: (e: any) => window.alert(`Couldn't record: ${e?.message ?? e}`),
  });

  return (
    <Card className="mb-4">
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Approval &amp; history</div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note — e.g. 'PL cert sighted, expires Dec'" className="flex-1 min-w-[220px]" />
            {!isApproved && <Button size="sm" icon={BadgeCheck} disabled={act.isPending} onClick={() => act.mutate("approved")}>Approve</Button>}
            {isApproved && <Button size="sm" variant="secondary" disabled={act.isPending} onClick={() => act.mutate("suspended")}>Suspend</Button>}
            <Button size="sm" variant="secondary" disabled={act.isPending} onClick={() => act.mutate("info_requested")}>Request info</Button>
            <Button size="sm" variant="secondary" disabled={act.isPending || !note.trim()} onClick={() => act.mutate("note")}>Add note</Button>
          </div>
        )}
        {events.length === 0 ? (
          <div className="text-[12px] text-text-muted">No decisions recorded yet.</div>
        ) : (
          <div className="space-y-1">
            {events.map((e: any) => (
              <div key={e.event_id} className="text-[12px] flex items-center gap-3 py-1 border-b border-line last:border-b-0 flex-wrap">
                <Pill label={String(e.event_type).replace(/_/g, " ")} tone={e.event_type === "approved" ? "won" : e.event_type === "suspended" ? "lost" : "default"} small />
                <span className="text-text flex-1 min-w-0">{e.note ?? ""}</span>
                <span className="text-text-muted font-mono">{fmtDate(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Workers card — T-381 Slice 2. The Ramsamy use case: each worker + tickets
// with expiries, ready to copy into a client RRF.
// -----------------------------------------------------------------------------
export const TICKET_TYPES = [
  "Driver licence (C)", "MR licence", "HR licence", "HC licence", "MC licence",
  "Crane operator — slewing (C6/C1/C0)", "Crane operator — non-slewing (CN)", "Vehicle loading crane (CV)",
  "Dogging (DG)", "Rigging basic (RB)", "Rigging intermediate (RI)", "Rigging advanced (RA)",
  "EWP (>11m)", "Telehandler", "Excavator", "Skid steer", "Forklift (LF)",
  "Service locator (DBYD/Telstra)", "White card", "Working at heights", "Confined space",
  "First aid", "VOC", "Electrical licence", "Other (type it in)",
];

function WorkersCard({ companyId, canEdit }: { companyId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [w, setW] = useState<any>({ first_name: "", last_name: "", job_title: "", date_of_birth: "", email: "", mobile: "", postcode: "" });
  const [busy, setBusy] = useState(false);
  const [ticketFor, setTicketFor] = useState<string | null>(null);   // worker_id with the add-ticket row open
  const [t, setT] = useState<any>({ ticket_type: "", ticket_other: "", ticket_number: "", issue_date: "", expiry_date: "" });
  const [tFile, setTFile] = useState<File | null>(null);        // front (or the only file)
  const [tBack, setTBack] = useState<File | null>(null);        // back of the card, optional
  const tInput = useRef<HTMLInputElement>(null);
  const tBackInput = useRef<HTMLInputElement>(null);
  // 15 Sep 2026 — James: "add the Front/Back upload we have on employees here
  // so AI worker can read it that way not just bulk". Front uploads on pick and
  // goes through /licences/ai-extract (bucket ap-documents) like the staff
  // wizard; the back is upload-only. Same folder the bulk screen uses.
  type TAi = "idle" | "uploading" | "reading" | "done" | "error";
  const [tAi, setTAi] = useState<TAi>("idle");
  const [tFrontPath, setTFrontPath] = useState<string | null>(null);   // uploaded on pick; save reuses it
  const [tConf, setTConf] = useState<number | null>(null);
  const [tHolder, setTHolder] = useState<string | null>(null);
  const [tNote, setTNote] = useState<string | null>(null);
  const [tBoth, setTBoth] = useState(false);                           // one file shows front AND back
  const [tAiErr, setTAiErr] = useState<string | null>(null);

  const { data: workers = [] } = useQuery({
    queryKey: ["subbie_workers", companyId],
    queryFn: async () => {
      const { data } = await supabase.from("subbie_workers")
        .select("*, subbie_worker_tickets(*)")
        .eq("subcontractor_company_id", companyId)
        .eq("is_active", true)
        .order("last_name");
      return (data ?? []) as any[];
    },
  });

  async function saveWorker() {
    if (!w.first_name.trim() || !w.last_name.trim()) { window.alert("Worker needs a first + last name."); return; }
    setBusy(true);
    try {
      const { error } = await supabase.from("subbie_workers").insert({
        subcontractor_company_id: companyId,
        first_name: w.first_name.trim(), last_name: w.last_name.trim(),
        job_title: w.job_title.trim() || null,
        date_of_birth: w.date_of_birth || null,
        email: w.email.trim() || null, mobile: w.mobile.trim() || null,
        postcode: w.postcode.trim() || null,
      });
      if (error) throw error;
      setW({ first_name: "", last_name: "", job_title: "", date_of_birth: "", email: "", mobile: "", postcode: "" });
      setAdding(false);
      qc.invalidateQueries({ queryKey: ["subbie_workers", companyId] });
    } catch (e: any) { window.alert(`Couldn't add worker: ${e.message ?? e}`); }
    finally { setBusy(false); }
  }

  function resetTicketForm() {
    setT({ ticket_type: "", ticket_other: "", ticket_number: "", issue_date: "", expiry_date: "" });
    setTFile(null); setTBack(null); setTFrontPath(null); setTAi("idle"); setTConf(null); setTHolder(null); setTNote(null); setTBoth(false); setTAiErr(null);
  }
  function openTicketForm(workerId: string) {
    resetTicketForm();
    setTicketFor(ticketFor === workerId ? null : workerId);
  }
  // The AI names a licence_types row; ticket_type here is free text from
  // TICKET_TYPES. Match loosely, else drop it into "Other" with the name typed.
  const looseType = (v: string) => v.toLowerCase().replace(/\(.*?\)/g, "").replace(/[^a-z0-9]/g, "");
  function ticketTypeFromAi(typeName: string): { ticket_type: string; ticket_other: string } {
    if (!typeName) return { ticket_type: "", ticket_other: "" };
    const want = looseType(typeName);
    const hit = TICKET_TYPES.find(k => !k.startsWith("Other") && (looseType(k) === want || looseType(k).includes(want) || want.includes(looseType(k))));
    return hit ? { ticket_type: hit, ticket_other: "" } : { ticket_type: TICKET_TYPES[TICKET_TYPES.length - 1], ticket_other: typeName };
  }
  function nameLooksLike(cardName: string | null, wk: any): boolean | null {
    if (!cardName || !wk) return null;
    const norm = (v: string) => v.toLowerCase().replace(/[^a-z ]/g, " ").split(/\s+/).filter(Boolean);
    const card = norm(cardName);
    const f = wk.first_name ? norm(wk.first_name)[0] : null;
    const l = wk.last_name ? norm(wk.last_name).slice(-1)[0] : null;
    return (!f || card.includes(f)) && (!l || card.includes(l));
  }

  async function pickFront(workerId: string, f: File | null) {
    setTFile(f); setTFrontPath(null); setTAi("idle"); setTConf(null); setTHolder(null); setTNote(null); setTBoth(false); setTAiErr(null);
    if (!f) return;
    try {
      setTAi("uploading");
      const safe = f.name.replace(/[^\w.\-]/g, "_");
      const path = `vendor-compliance/worker-tickets/${workerId}/${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage.from("ap-documents").upload(path, f, { upsert: false });
      if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
      setTFrontPath(path);

      setTAi("reading");
      const res = await apiFetch("/licences/ai-extract", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ storage_path: path, bucket: "ap-documents" }),
      });
      if (!res.ok) { const txt = await res.text().catch(() => ""); throw new Error(`Couldn't read it (${res.status}): ${txt.slice(0, 200)}`); }
      const ai = await res.json() as {
        confidence: number; licence_type_id: string | null;
        extracted: { licence_number?: string | null; issue_date?: string | null; expiry_date?: string | null; notes?: string | null; side?: string | null; holder_name?: string | null };
      };
      let typeName = "";
      if (ai.licence_type_id) {
        const { data: lt } = await supabase.from("licence_types").select("display_name").eq("licence_type_id", ai.licence_type_id).maybeSingle();
        typeName = (lt as any)?.display_name ?? "";
      }
      const x = ai.extracted ?? {};
      const fromAi = ticketTypeFromAi(typeName);
      // Pre-fill only what the manager hasn't typed — same courtesy as the licence wizard.
      setT((prev: any) => ({
        ...prev,
        ticket_type:   prev.ticket_type || fromAi.ticket_type,
        ticket_other:  prev.ticket_other || (prev.ticket_type ? "" : fromAi.ticket_other),
        ticket_number: prev.ticket_number || (x.licence_number ?? ""),
        issue_date:    prev.issue_date || (x.issue_date ?? ""),
        expiry_date:   prev.expiry_date || (x.expiry_date ?? ""),
      }));
      setTConf(ai.confidence ?? null); setTHolder(x.holder_name ?? null); setTNote(x.notes ?? null);
      setTBoth((x.side ?? "unknown") === "both");
      setTAi("done");
    } catch (e: any) {
      setTAi("error"); setTAiErr(e?.message ?? "Couldn't read it");
    }
  }

  async function saveTicket(workerId: string) {
    if (!t.ticket_type) { window.alert("Pick the ticket type."); return; }
    const finalType = t.ticket_type.startsWith("Other") ? (t.ticket_other.trim() || "Other") : t.ticket_type;
    setBusy(true);
    try {
      const folder = `vendor-compliance/worker-tickets/${workerId}`;
      let path: string | null = tFrontPath;   // already uploaded on pick (AI read)
      if (tFile && !path) {
        const safe = tFile.name.replace(/[^\w.\-]/g, "_");
        path = `${folder}/${Date.now()}-${safe}`;
        const { error: upErr } = await supabase.storage.from("ap-documents").upload(path, tFile, { upsert: false });
        if (upErr) { path = null; window.alert(`Cert upload failed (${upErr.message}) — saving the ticket without it.`); }
      }
      let backPath: string | null = null;
      if (tBack && path) {
        const safe = tBack.name.replace(/[^\w.\-]/g, "_");
        backPath = `${folder}/${Date.now()}-back-${safe}`;
        const { error: upErr } = await supabase.storage.from("ap-documents").upload(backPath, tBack, { upsert: false });
        if (upErr) { backPath = null; window.alert(`Back upload failed (${upErr.message}) — saving with the front only.`); }
      }
      // mig 377: one scanned file showing both sides points the back at the same file (staff convention)
      const back = backPath ?? (tBoth && path ? path : null);
      const { error } = await supabase.from("subbie_worker_tickets").insert({
        worker_id: workerId,
        ticket_type: finalType,
        ticket_number: t.ticket_number.trim() || null,
        issue_date: t.issue_date || null,
        expiry_date: t.expiry_date || null,
        document_path: path, document_name: tFile?.name ?? null,
        back_document_path: back, back_document_name: backPath ? (tBack?.name ?? null) : (back ? (tFile?.name ?? null) : null),
      });
      if (error) throw error;
      resetTicketForm(); setTicketFor(null);
      qc.invalidateQueries({ queryKey: ["subbie_workers", companyId] });
    } catch (e: any) { window.alert(`Couldn't add ticket: ${e.message ?? e}`); }
    finally { setBusy(false); }
  }

  async function viewTicketDoc(path: string) {
    const { data } = await supabase.storage.from("ap-documents").createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  }

  async function removeWorker(worker: any) {
    if (!window.confirm(`Remove ${worker.first_name} ${worker.last_name}? Their tickets go too.`)) return;
    await supabase.from("subbie_workers").update({ is_active: false }).eq("worker_id", worker.worker_id);
    qc.invalidateQueries({ queryKey: ["subbie_workers", companyId] });
  }

  return (
    <Card className="mb-4">
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Workers &amp; tickets</div>
          {canEdit && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(a => !a)}>{adding ? "Close" : "Add worker"}</Button>}
        </div>
        <p className="text-[11px] text-text-muted mb-3">
          The people this subbie puts on our sites, with their tickets + expiries — fields match the client RRFs (Ventia), so role requests become copy-out.
        </p>

        {adding && (
          <div className="mb-3 border border-line rounded-md p-3 bg-ink-raised space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="First name"><Input value={w.first_name} onChange={e => setW({ ...w, first_name: e.target.value })} /></Field>
              <Field label="Last name"><Input value={w.last_name} onChange={e => setW({ ...w, last_name: e.target.value })} /></Field>
              <Field label="Job title" hint=""><Input value={w.job_title} onChange={e => setW({ ...w, job_title: e.target.value })} placeholder="Crane Operator" /></Field>
              <Field label="Date of birth" hint=""><Input type="date" value={w.date_of_birth} onChange={e => setW({ ...w, date_of_birth: e.target.value })} /></Field>
              <Field label="Email" hint=""><Input value={w.email} onChange={e => setW({ ...w, email: e.target.value })} /></Field>
              <Field label="Mobile" hint=""><Input value={w.mobile} onChange={e => setW({ ...w, mobile: e.target.value })} /></Field>
              <Field label="Postcode" hint=""><Input value={w.postcode} onChange={e => setW({ ...w, postcode: e.target.value })} /></Field>
              <div className="flex items-end"><Button size="sm" disabled={busy} onClick={saveWorker}>{busy ? "Saving…" : "Save worker"}</Button></div>
            </div>
          </div>
        )}

        {workers.length === 0 ? (
          <div className="text-[12px] text-text-muted">No workers recorded — add the crew this subbie sends to site.</div>
        ) : (
          <div className="space-y-2">
            {workers.map((wk: any) => (
              <div key={wk.worker_id} className="border border-line rounded-md p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-medium text-text">{wk.first_name} {wk.last_name}</span>
                  {wk.job_title && <span className="text-[12px] text-text-muted">{wk.job_title}</span>}
                  {wk.mobile && <span className="text-[11px] text-text-muted font-mono">{wk.mobile}</span>}
                  <span className="ml-auto flex items-center gap-1.5">
                    {canEdit && (
                      <>
                        <button type="button" onClick={() => openTicketForm(wk.worker_id)}
                          className="text-[11px] text-accent hover:underline">+ ticket</button>
                        {/* 15 Sep 2026 — the folder-of-tickets path, same AI reader as staff licences */}
                        <Link to={`/subbies/${companyId}/workers/${wk.worker_id}/bulk`} className="text-[11px] text-accent hover:underline">bulk tickets</Link>
                        <button type="button" onClick={() => removeWorker(wk)} className="text-text-muted hover:text-danger p-1"><Trash2 size={12} /></button>
                      </>
                    )}
                  </span>
                </div>
                {/* ticket chips */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(wk.subbie_worker_tickets ?? []).length === 0 && <span className="text-[11px] text-text-muted">no tickets recorded</span>}
                  {(wk.subbie_worker_tickets ?? []).map((tk: any) => (
                    <span key={tk.ticket_id} className="inline-flex items-center gap-0.5">
                    <button type="button"
                      onClick={() => tk.document_path ? viewTicketDoc(tk.document_path) : undefined}
                      title={[tk.ticket_number, tk.expiry_date ? `expires ${fmtDate(tk.expiry_date)}` : "no expiry",
                        tk.document_path ? (tk.back_document_path ? (tk.back_document_path === tk.document_path ? "click for cert (both sides in one file)" : "click for front") : "click for cert") : "no cert file"].filter(Boolean).join(" · ")}
                      className={`px-2 py-0.5 rounded-full text-[11px] border ${expiryClass(tk.expiry_date) === "text-danger" ? "bg-danger/10 border-danger/40 text-danger"
                        : expiryClass(tk.expiry_date) === "text-warn" ? "bg-warn/10 border-warn/40 text-warn"
                        : "bg-ok/10 border-ok/30 text-ok"} ${tk.document_path ? "cursor-pointer" : "cursor-default"}`}>
                      {tk.ticket_type}{tk.expiry_date ? ` · ${fmtDate(tk.expiry_date)}` : ""}
                    </button>
                    {tk.back_document_path && tk.back_document_path !== tk.document_path && (
                      <button type="button" title={`back: ${tk.back_document_name ?? ""}`} onClick={() => viewTicketDoc(tk.back_document_path)}
                        className="text-[10px] text-text-muted hover:text-accent hover:underline px-0.5">back</button>
                    )}
                    </span>
                  ))}
                </div>
                {/* add ticket row — front/back like the staff wizard, AI reads the front on pick (15 Sep 2026) */}
                {ticketFor === wk.worker_id && canEdit && (
                  <div className="mt-2 border-t border-line pt-2 space-y-2">
                    <div className="flex items-end gap-2 flex-wrap">
                      <button type="button" onClick={() => tInput.current?.click()} disabled={tAi === "uploading" || tAi === "reading"}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[12px] transition-colors mb-0.5 ${tFile ? "border-accent/40 text-text" : "border-line text-text hover:border-accent"}`}>
                        <Upload size={13} /> {tFile ? tFile.name : "Front"}
                      </button>
                      <input ref={tInput} type="file" accept="application/pdf,image/*" capture="environment" className="hidden"
                        onChange={e => { pickFront(wk.worker_id, e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
                      {!tBoth && (
                        <>
                          <button type="button" onClick={() => tBackInput.current?.click()} disabled={!tFile}
                            title={tFile ? "Back of the card (optional)" : "Pick the front first"}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[12px] transition-colors mb-0.5 disabled:opacity-50 ${tBack ? "border-accent/40 text-text" : "border-line text-text hover:border-accent"}`}>
                            <Upload size={13} /> {tBack ? tBack.name : "Back (optional)"}
                          </button>
                          <input ref={tBackInput} type="file" accept="application/pdf,image/*" capture="environment" className="hidden"
                            onChange={e => { setTBack(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
                        </>
                      )}
                      {tBack && <button type="button" onClick={() => setTBack(null)} className="text-[11px] text-text-muted hover:text-danger mb-1.5">remove back</button>}
                      {tFile && !tBack && (
                        <label className="inline-flex items-center gap-1.5 text-[11px] text-text-muted cursor-pointer select-none mb-1.5">
                          <input type="checkbox" checked={tBoth} onChange={e => setTBoth(e.target.checked)} className="accent-accent" /> both sides in this file
                        </label>
                      )}
                      {tAi === "uploading" && <span className="inline-flex items-center gap-1 text-[11px] text-text-muted mb-1.5"><Spinner size={11} /> uploading…</span>}
                      {tAi === "reading"   && <span className="inline-flex items-center gap-1 text-[11px] text-text-muted mb-1.5"><Sparkles size={11} className="text-accent" /> reading the card…</span>}
                      {tAi === "done" && tConf != null && (
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] mb-1 ${tConf < 0.6 ? "border-warn/30 text-warn bg-warn/5" : "border-ok/30 text-ok bg-ok/5"}`}>
                          <Sparkles size={11} /> read — {Math.round(tConf * 100)}% sure, check it
                        </span>
                      )}
                      {tAi === "done" && tHolder && nameLooksLike(tHolder, wk) === false && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-warn/30 text-warn bg-warn/5 px-2 py-0.5 text-[11px] mb-1">
                          <AlertTriangle size={11} /> name on card: {tHolder}
                        </span>
                      )}
                      {tAi === "error" && <span className="text-[11px] text-danger mb-1.5">{tAiErr} — fill it in by hand.</span>}
                    </div>
                    <div className="flex items-end gap-2 flex-wrap">
                      <Field label="Ticket">
                        <select value={t.ticket_type} onChange={e => setT({ ...t, ticket_type: e.target.value })}
                          className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                          <option value="">— type —</option>
                          {TICKET_TYPES.map(x => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </Field>
                      {t.ticket_type.startsWith("Other") && (
                        <Field label="Ticket name" hint=""><Input value={t.ticket_other} onChange={e => setT({ ...t, ticket_other: e.target.value })} placeholder="e.g. Traffic control" className="max-w-[160px]" /></Field>
                      )}
                      <Field label="Number" hint=""><Input value={t.ticket_number} onChange={e => setT({ ...t, ticket_number: e.target.value })} className="max-w-[130px]" /></Field>
                      <Field label="Issued" hint=""><Input type="date" value={t.issue_date} onChange={e => setT({ ...t, issue_date: e.target.value })} /></Field>
                      <Field label="Expiry" hint=""><Input type="date" value={t.expiry_date} onChange={e => setT({ ...t, expiry_date: e.target.value })} /></Field>
                      <Button size="sm" disabled={busy || tAi === "uploading" || tAi === "reading"} onClick={() => saveTicket(wk.worker_id)} className="mb-0.5">{busy ? "…" : "Save"}</Button>
                      <button type="button" onClick={() => { resetTicketForm(); setTicketFor(null); }} className="text-[11px] text-text-muted hover:text-text mb-1.5">cancel</button>
                    </div>
                    {tNote && tAi === "done" && <div className="text-[11px] text-text-muted italic">AI: {tNote}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Client approvals card (migration 219)
// -----------------------------------------------------------------------------
function ClientApprovalsCard({ companyId, canEdit }: { companyId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState(false);
  const [clientId, setClientId] = useState("");
  const [reference, setReference] = useState("");
  const [approvedDate, setApprovedDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: approvals = [] } = useQuery({
    queryKey: ["subbie_client_approvals", companyId],
    queryFn: async () => {
      const { data } = await supabase.from("subbie_client_approvals")
        .select("*, clients:client_id(client_code, legal_name, trading_name)")
        .eq("subcontractor_company_id", companyId)
        .order("created_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ["clients_for_approvals"],
    enabled: adding,
    queryFn: async () => {
      const { data } = await supabase.from("clients")
        .select("client_id, client_code, legal_name, trading_name").order("legal_name");
      return (data ?? []) as any[];
    },
  });

  async function save() {
    if (!clientId) { window.alert("Pick the client who issued the approval."); return; }
    setBusy(true);
    try {
      let path: string | null = null;
      if (file) {
        const safe = file.name.replace(/[^\w.\-]/g, "_");
        path = `vendor-compliance/client-approvals/${companyId}/${Date.now()}-${safe}`;
        const { error: upErr } = await supabase.storage.from("ap-documents").upload(path, file, { upsert: false });
        if (upErr) { path = null; window.alert(`Doc upload failed (${upErr.message}) — saving the approval without it.`); }
      }
      const { error } = await supabase.from("subbie_client_approvals").insert({
        subcontractor_company_id: companyId,
        client_id: clientId,
        reference: reference.trim() || null,
        approved_date: approvedDate || null,
        expiry_date: expiryDate || null,
        document_path: path,
        document_name: file?.name ?? null,
      });
      if (error) throw error;
      setAdding(false); setClientId(""); setReference(""); setApprovedDate(""); setExpiryDate(""); setFile(null);
      qc.invalidateQueries({ queryKey: ["subbie_client_approvals", companyId] });
    } catch (e: any) {
      window.alert(`Couldn't save: ${e.message ?? e}`);
    } finally { setBusy(false); }
  }

  async function viewDoc(a: any) {
    if (!a.document_path) return;
    const { data } = await supabase.storage.from("ap-documents").createSignedUrl(a.document_path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  }

  async function remove(a: any) {
    if (!window.confirm("Remove this client approval record?")) return;
    await supabase.from("subbie_client_approvals").delete().eq("approval_id", a.approval_id);
    qc.invalidateQueries({ queryKey: ["subbie_client_approvals", companyId] });
  }

  return (
    <Card className="mb-4">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Client approvals</div>
          {canEdit && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(a => !a)}>{adding ? "Close" : "Add approval"}</Button>}
        </div>
        <p className="text-[11px] text-text-muted mb-3">
          Tier-1 sign-offs to use this subbie (e.g. Ventia secondary-subcontractor approval) — letter + dates, checked at booking time.
        </p>

        {adding && (
          <div className="mb-3 border border-line rounded-md p-3 bg-ink-raised space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Client">
                <select value={clientId} onChange={e => setClientId(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                  <option value="">— pick —</option>
                  {clients.map((c: any) => <option key={c.client_id} value={c.client_id}>{c.trading_name || c.legal_name}</option>)}
                </select>
              </Field>
              <Field label="Reference" hint=""><Input value={reference} onChange={e => setReference(e.target.value)} placeholder="approval ref" /></Field>
              <Field label="Approved"><Input type="date" value={approvedDate} onChange={e => setApprovedDate(e.target.value)} /></Field>
              <Field label="Expiry" hint=""><Input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} /></Field>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => fileInput.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-[12px] text-text hover:border-accent transition-colors">
                <Upload size={13} /> {file ? file.name : "Attach approval letter"}
              </button>
              <input ref={fileInput} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => setFile(e.target.files?.[0] ?? null)} />
              <Button size="sm" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save approval"}</Button>
            </div>
          </div>
        )}

        {approvals.length === 0 ? (
          <div className="text-[12px] text-text-muted">None recorded.</div>
        ) : (
          <div className="space-y-1">
            {approvals.map((a: any) => (
              <div key={a.approval_id} className="text-[12px] flex items-center gap-3 py-1.5 border-b border-line last:border-b-0 flex-wrap">
                <span className="font-medium text-text">{a.clients?.trading_name || a.clients?.legal_name || "Client"}</span>
                {a.reference && <span className="font-mono text-text-muted">{a.reference}</span>}
                {a.approved_date && <span className="text-text-muted">approved {fmtDate(a.approved_date)}</span>}
                <span className={`ml-auto font-mono ${expiryClass(a.expiry_date)}`}>{a.expiry_date ? `expires ${fmtDate(a.expiry_date)}` : "no expiry"}</span>
                {a.document_path && (
                  <button type="button" onClick={() => viewDoc(a)} className="text-accent hover:underline inline-flex items-center gap-1"><FileText size={12} /> letter</button>
                )}
                {canEdit && (
                  <button type="button" onClick={() => remove(a)} className="text-text-muted hover:text-danger p-1"><Trash2 size={12} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
